import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, User, Building2, Briefcase, PieChart, ChevronRight, Loader2 } from "lucide-react";
import { Badge, Input, cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { searchPersons, type ArPersonHit } from "../../../lib/ariregister";

export function PersonSearch() {
  const t = useT();
  const [input, setInput] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState<ArPersonHit[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setQ(input.trim()), 350);
    return () => clearTimeout(id);
  }, [input]);

  useEffect(() => {
    if (q.length < 2) {
      setItems([]);
      return;
    }
    let alive = true;
    setLoading(true);
    searchPersons(q)
      .then((r) => alive && setItems(r.items))
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [q]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("ariregister.person.searchTitle")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("ariregister.person.searchIntro")}</p>
        </div>
        <Link to="/ariregister" className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card/80 px-3 py-1.5 text-sm shadow-soft backdrop-blur hover:bg-card">
          <Building2 className="size-4" /> {t("ariregister.person.toCompanies")}
        </Link>
      </div>

      <div className="rounded-xl border border-border bg-card/80 p-4 shadow-soft backdrop-blur">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t("ariregister.person.searchPlaceholder")} className="pl-9" autoFocus />
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card/80 shadow-soft backdrop-blur">
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5 text-sm text-muted-foreground">
          <span>{q.length < 2 ? t("ariregister.person.hint") : t("ariregister.person.results", { total: items.length })}</span>
          {loading && <Loader2 className="size-4 animate-spin" />}
        </div>
        {items.length === 0 ? (
          <div className="px-6 py-14 text-center text-sm text-muted-foreground">
            {q.length < 2 ? t("ariregister.person.hint") : t("ariregister.person.empty")}
          </div>
        ) : (
          <ul className="divide-y divide-border/60">
            {items.map((p) => (
              <li key={p.hash}>
                <Link to={`/ariregister/isik/${p.hash}`} className="group flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-accent/40">
                  <span className="inline-flex size-8 items-center justify-center rounded-full bg-primary/15 text-primary">
                    <User className="size-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{p.name}</div>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      {p.isBoard && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Briefcase className="size-3" /> {t("ariregister.person.isBoard")}
                        </span>
                      )}
                      {p.isOwner && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <PieChart className="size-3" /> {t("ariregister.person.isOwner")}
                        </span>
                      )}
                    </div>
                  </div>
                  <Badge variant={p.companies > 1 ? "default" : "outline"} className={cn(p.companies > 1 && "font-semibold")}>
                    {t("ariregister.person.companies", { count: p.companies })}
                  </Badge>
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
